import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { format } from 'date-fns';
import { Card } from '@frontend/shadcn/components/ui/card';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@frontend/shadcn/components/ui/popover';
import { Checkbox } from '@frontend/shadcn/components/ui/checkbox';
import { DataTable } from '@frontend/app/refresh-components/DataTableV2/Table';
import { TablePagination } from '@frontend/app/refresh-components/DataTableV2/TablePagination';
import { faPencil, } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ChevronDownIcon, ChevronUpIcon, ArrowDownArrowUpIcon, XmarkIcon, ArrowDownToLineIcon, UpRightFromSquareIcon, EllipsisIcon, SplitIcon, TriangleExclamationIcon, ChartColumnIcon, TrashCanIcon, TableColumnsIcon, CopyIcon } from '@revfluence/fresh-icons/regular/esm';
import { Tooltip, TooltipContent, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { SortingState } from '@tanstack/react-table';
import { formatValue, DataFormat, defaultAffiliateSort, abbreviateAffiliateLink, SHOPIFY_ADMIN_BASE_URL, PROMO_FILTERS, LINK_FILTERS } from '@affiliates/utils';
import { OFFER_PROMO_CODE_STATUS, OFFER_SOURCE, OFFER_STATUS, OFFER_PAYOUT_TYPE } from '@affiliates/types/globalTypes';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import { isEmpty, map, trim, isNull, lowerCase, startCase, isString, filter, includes } from 'lodash';
import { InfoCircleFilled, SearchOutlined } from '@ant-design/icons';
import Tag from '@frontend/app/refresh-components/Tag';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@frontend/shadcn/components/ui/dropdown-menu';
import { Col, message, Row, Typography } from '@revfluence/fresh';
import { Modal } from 'antd';
import { UserPlusIcon } from '@revfluence/fresh-icons/regular';
import { OFFER_STATUS_TYPE } from '../../utils/getTagType';
import { composePromoCodeStatus } from './components/composePromoCodeStatus';
import { composeLinkStatus } from './components/composeLinkStatus';
import { TOfferMember, TMemberTableRow, TMemberTableCSVRow, TSelectedAffliate, MemberStatus, OfferMemberStatus } from './types';
import { memberIsPending } from './memberIsPending';
import { getPendingActionFromMembers, isOperationPending, PendingAction, PendingMembers } from './PendingMembers';
import { FailedNotice } from './FailedNotice';
import { SuccessNotice } from './SuccessNotice';
import { PromoCodeErrorMessage } from './PromoCodeErrorMessage';
import { ZeroState } from './ZeroState';
import { NameCell } from './NameCell';
import { AdditionalDeeplinkTags } from './AdditionalDeeplinksTags';
import { DeeplinkDrawer } from '../DeeplinkDrawer/DeeplinkDrawer';
import { IHeader } from '../../hooks';
import { GetOfferAffliatesStatsMultipleShopify_affiliatesStats } from '../../queries/types/GetOfferAffliatesStatsMultipleShopify';
import { GetOfferById_offer } from '../../queries/types/GetOfferById';
import { extractStoreName } from '../../utils/extractShopifyStoreName';
import { RefreshCreateAdditionalDeepLinks } from './Buttons/CreateDeepLinks/RefreshCreateAdditionalDeepLinks';
import { getTagColor } from '../../constants/offers';

const { Text } = Typography;

const toLocale = (date) => {
  if (isNull(date)) return '-';
  const dateDate = new Date(date);
  return dateDate.toLocaleString('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
};

// Helper Components
const SortButton = ({ column, children }: { column; children: React.ReactNode }) => {
  const isSorted = column.getIsSorted();
  const [isHovering, setIsHovering] = React.useState(false);

  const handleSort = () => {
    if (isSorted === 'asc') {
      column.toggleSorting(true);
    } else if (isSorted === 'desc') {
      column.clearSorting();
    } else {
      column.toggleSorting(false);
    }
  };

  return (
    <Button
      variant="ghost"
      className="px-2 hover:bg-transparent w-full"
      onClick={handleSort}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="flex w-full justify-between items-center">
        <span>{children}</span>
        <span className="h-3 w-3 flex items-center justify-center">
          {isSorted === 'asc' && <ChevronUpIcon className="h-3 w-3" />}
          {isSorted === 'desc' && <ChevronDownIcon className="h-3 w-3" />}
          {!isSorted && isHovering && <ArrowDownArrowUpIcon className="h-3 w-3" />}
        </span>
      </div>
    </Button>
  );
};

interface IProps {
  buttonActions: {
    addMembers: () => void;
    export: (columns: IHeader[], data: readonly TMemberTableCSVRow[]) => void;
    pause: () => void;
    resume: (refreshDates: boolean) => void;
    openMemberConversionDrawer?: (selectedAffliate: TSelectedAffliate) => void;
    refresh?: () => void;
    storeWiseStats?: () => void;
    storeLevelAffiliateStats?: () => void;
    deleteAffiliatesFromAnOffer?: (selectedMemberIds: number[], offerId: number, offerSource: string, setIsDeletedAction: (isDeletedAction: boolean) => void) => void;
  };
  emailComposeButton: React.ReactElement;
  fixFailedMembers: () => void;
  members: readonly TOfferMember[];
  missingShopifyCredentials: boolean;
  offer: GetOfferById_offer;
  offerSource: OFFER_SOURCE;
  offerStatus: OFFER_STATUS;
  onSelectMembers: (memberIds: number[]) => void;
  selectedAffiliateIds: number[];
  failedPromoCodeErrors: OFFER_PROMO_CODE_STATUS[];
  currencies?: string[];
  showStoreLevelAffiliateStats: boolean;
  connectedStores: { storeName: string; clientShopifyConnectionId: number }[];
  affiliatesStats: GetOfferAffliatesStatsMultipleShopify_affiliatesStats[];
  shopifyStoreName: string;
  onComplete: () => void;
  isMultipleShopifyOffer: boolean;
  selectedPayoutIds: number[];
}
interface SuccessNoticeState {
  show: boolean;
  count: number;
  action: PendingAction | null;
}

// Utility Functions
function extractDiscountCodeId(externalDiscountCodeGid: string) {
  if (externalDiscountCodeGid) {
    const idPattern = /\/(\d+)$/;
    const match = externalDiscountCodeGid.match(idPattern);
    return match ? match[1] : null;
  }
  return null;
}

// Render Functions
const renderPayoutVariant = (payoutId: number | null, returnJsx: boolean = true, payouts) => {
  const payout = payouts.find((payout) => payout.id === payoutId);
  if (!payout) return '';
  let text = '';
  switch (payout.payoutType) {
    case OFFER_PAYOUT_TYPE.CLICK:
      text = `${payout.label} ($${payout.flatPayout})`;
      break;
    case OFFER_PAYOUT_TYPE.CONVERSION:
      text = `${payout.label} ($${payout.flatPayout})`;
      break;
    case OFFER_PAYOUT_TYPE.SALE:
      text = `${payout.label} (${payout.percentPayout}%)`;
      break;
    case OFFER_PAYOUT_TYPE.CONVERSION_AND_SALE:
      text = `${payout.label} ($${payout.flatPayout}+${payout.percentPayout}%)`;
      break;
    default:
      break;
  }
  if (!returnJsx) {
    return text;
  }
  return <Tag label={text} bgColor="bg-grey-2" textColor="text-primary" />;
};

const renderShopifyColumn = (shopifyStoreName: string, selectedAffliate, offer: GetOfferById_offer, offerSource: string) => {
  if (offerSource !== OFFER_SOURCE.SHOPIFY) {
    return null;
  }
  const storeName = extractStoreName(shopifyStoreName);
  const currentPromo = offer.promos[0];
  const isUngrouped = currentPromo.isUngrouped;

  let discountCodeId;
  if (isUngrouped) {
    discountCodeId = extractDiscountCodeId(selectedAffliate?.selectedPromo?.providerMetadata?.ungroupConfig?.discountCodeGid);
  } else {
    discountCodeId = `${extractDiscountCodeId(currentPromo?.discountCodeGId)}/codes`;
    if (selectedAffliate.code) {
      discountCodeId = `${discountCodeId}?query=${selectedAffliate.code}`;
    }
  }
  if (!discountCodeId) return null;

  const url = `${SHOPIFY_ADMIN_BASE_URL}${storeName}/discounts/${discountCodeId}`;
  return (
    <Tooltip>
      <TooltipTrigger>
        <Button
          variant="ghost"
          size="icon"
          asChild
          className="h-8 w-8"
        >
          <a href={url} target="_blank" rel="noopener noreferrer">
            <UpRightFromSquareIcon className="h-4 w-4 text-primary" />
          </a>
        </Button>
      </TooltipTrigger>
      <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]">View promo code in Shopify store</TooltipContent>
    </Tooltip>

  );
};

const renderStatsColumn = (selectedAffliate: TSelectedAffliate, buttonActions) => {
  return (
    <Tooltip>
      <TooltipTrigger>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => buttonActions.openMemberConversionDrawer(selectedAffliate)}
          className="h-8 w-8"
        >
          <ChartColumnIcon className="h-4 w-4 text-primary" />
        </Button>
      </TooltipTrigger>
      <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]">View Member Conversions</TooltipContent>
    </Tooltip>

  );
};

const renderStatusColumn = (status: string, linkStatusError: string, isDeletedAction: boolean) => {
  const displayStatus = isDeletedAction && lowerCase(status) === lowerCase(OFFER_STATUS_TYPE.DEACTIVATING)
    ? OFFER_STATUS_TYPE.DELETING
    : status;
  const statusTag = <Tag label={startCase(displayStatus)} bgColor={getTagColor(displayStatus)} textColor="text-primary" />;

  return lowerCase(status) === lowerCase(OFFER_STATUS_TYPE.FAILED_UPDATE) ? (
    <Tooltip>
      <TooltipTrigger>{statusTag}</TooltipTrigger>
      <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]">{linkStatusError}</TooltipContent>
    </Tooltip>
  ) : (
    statusTag
  );
};

const renderColumsWithBase = (prefix: string, usdValue: number, baseValue?: number, baseCurrency?: string) => {
  if (baseValue && baseCurrency !== 'USD') {
    return (
      <div className="flex flex-col gap-1">
        <Text>{`${prefix} ${usdValue}`}</Text>
        <Text className="text-sm text-muted-foreground">{`${baseValue} ${baseCurrency}`}</Text>
      </div>
    );
  }
  return <Text>{`${prefix} ${usdValue}`}</Text>;
};

const renderCurrencyColumn = (data, toggleCurrency: boolean) => {
  if (toggleCurrency || data.currency) {
    return (
      <Row justify="center" align="middle">
        <Col span={24}>
          <Text>
            ${formatValue(DataFormat.Money, data.valueUsd)}
          </Text>
        </Col>
        <Col span={24}>
          <Text type="secondary">
            {data.currency} {formatValue(DataFormat.Money, data.valueBase)}
          </Text>
        </Col>
      </Row>
    );
  } else {
    return `$${formatValue(DataFormat.Money, data)}`;
  }
};

// Main Component
export const RefreshMemberTable: React.FC<Readonly<IProps>> = (props) => {
  const {
    buttonActions,
    emailComposeButton,
    fixFailedMembers,
    members,
    missingShopifyCredentials,
    offer,
    offerSource,
    offerStatus,
    onSelectMembers,
    selectedAffiliateIds,
    showStoreLevelAffiliateStats,
    connectedStores,
    affiliatesStats,
    shopifyStoreName,
    onComplete,
    selectedPayoutIds,
  } = props;

  // Hooks
  const {
    isManageDeepLinksAllowed,
    deleteCreatorFromOffer,
    linkForPromo,
    currencyConversionForLink,
    enableMultipleShopify: isEnabledMultipleShopify,
    multiCurrency: isMulticurrency,
    automatePayoutTier,
  } = useClientFeatures();
  // State
  const [searchText, setSearchText] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isDeeplinkDrawerVisible, setDeeplinkDrawerVisible] = useState(false);
  const [deeplinkData, setDeeplinkData] = useState<TMemberTableRow>(null);
  const [renderWithCurrency, setRenderWithCurrency] = useState(false);
  const [toggleCurrency, setToggleCurrency] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TMemberTableRow[]>([]);
  const [isDeletedAction, setIsDeletedAction] = useState(false);
  const [hasPendingOperation, setHasPendingOperation] = useState(false);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [successNotice, setSuccessNotice] = useState<SuccessNoticeState>({
    show: false,
    count: 0,
    action: null,
  });
  const payouts = useMemo(() => offer?.payouts ?? [], [offer]);
  // Memoized data transformations
  const filteredMembers = useMemo(() => {
    if (!selectedPayoutIds?.length) return members;
    return members.filter((member) => {
      // Handle special case for Automated Tiers (members with offerAutomationConfigId)
      if (selectedPayoutIds.includes(-1) && member.offerAutomationConfigId != null) {
        return true;
      }
      // Handle regular payout ID filtering
      return selectedPayoutIds.includes(member.payoutId);
    });
  }, [members, selectedPayoutIds]);
  const data = useMemo(() => {
    const sortedMembers = [...filteredMembers].sort(defaultAffiliateSort);
    return map(
      sortedMembers,
      (member: TOfferMember): TMemberTableRow => {
        let linkStatus = member.status;
        if (missingShopifyCredentials) {
          linkStatus = OfferMemberStatus.UNKNOWN;
        }
        const commonFields: TMemberTableRow = {
          _sortableFields: {
            affiliateName: trim(member.name).toLowerCase(),
          },
          key: member.memberId.toLocaleString(),
          affiliateName: {
            name: member.name,
            imageUrl: member.imageUrl,
            memberId: member.memberId,
            disabled: member.memberStatus === MemberStatus.DELETED,
            disabledReason:
              member.memberStatus === MemberStatus.DELETED ? 'This member has been deleted in the Members app' : null,
          },
          payoutId: {
            id: member.payoutId,
            automationConfigId: member?.offerAutomationConfigId,
          },
          avgSale: member.avgSale,
          conversions: member.conversions,
          email: member.email,
          endDate: null,
          id: String(member.memberId),
          linkStatus,
          payoutEarned: member.payoutEarned,
          payoutMade: member.payoutMade,
          sales: member.sales,
          startDate: null,
          disableSelection: member.memberStatus === MemberStatus.DELETED,
          disabledReason:
            member.memberStatus === MemberStatus.DELETED ? 'This member has been deleted in the Members app' : null,
          name: member.name,
          affiliateOffer: {
            id: member.affiliateOfferId,
            affiliateId: member.affiliateId,
          },
          createdDate: member.createdDate,
          memberId: member.memberId,
          landingPageUrl: member.landingPageUrl,
        };
        switch (member.source) {
          case OFFER_SOURCE.SHOPIFY:
            return {
              ...commonFields,
              ...(toggleCurrency
                ? {
                  avgSale: {
                    valueBase: member.avgSaleBase,
                    valueUsd: member.avgSale,
                    currency: member.currencies[0],
                  },
                  sales: {
                    valueBase: member.salesBase,
                    valueUsd: member.sales,
                    currency: member.currencies[0],
                  },
                  payoutEarned: {
                    valueBase: member.payoutEarnedBase,
                    valueUsd: member.payoutEarned,
                    currency: member.currencies[0],
                  },
                }
                : {}),
              externalCode: member.code,
              affiliateLink: member.affiliateLink,
              affiliateLinkAbbrev: {
                shortAffiliateLinkAbbrev: member.affiliateShortLink || abbreviateAffiliateLink(member.affiliateLink),
                shortLink: member.affiliateShortLink,
              },
              linkCreationDate: member.createdDate,
              selectedAffliate: {
                affiliateOfferId: member.affiliateOfferId,
                memberAffiliateOfferId: member.affiliateOfferId,
                name: member.name,
                source: member.source,
                imageUrl: member.imageUrl,
                isMultipleShopifyOffer: !!offer.promos[0]?.connectedClientMetadata?.length,
                status: composePromoCodeStatus(
                  member.status,
                  member.codeStartDate,
                  member.codeEndDate,
                  offer.promos[0],
                  offer.isNewFlow,
                  offer.archivedDate,
                  member.deletedDate,
                ),
                payout: member?.payoutId ? payouts.find((p) => p.id === member.payoutId) : null,
                code: member.code,
                affliateLevelStats: {
                  conversions: member.conversions,
                  sales: member.sales,
                  salesBase: member.salesBase,
                  avgSale: member.avgSale,
                  avgSaleBase: member.avgSaleBase,
                  payoutEarned: member.payoutEarned,
                  payoutEarnedBase: member.payoutEarnedBase,
                  payoutMade: member.payoutMade,
                  payOutDue: member.payoutEarned - member.payoutMade,
                  clicks: null,
                },
                externalDiscountCodeGid: member.externalDiscountCodeGid,
                providerMetadata: member.providerMetadata,
              },
              affliateDetailedStats:
                (showStoreLevelAffiliateStats
                  && affiliatesStats?.find((a) => a.affiliateId === member.affiliateId)?.storeStats)
                || [],
              endDate: member.codeEndDate,
              startDate: member.codeStartDate,
              linkStatus: composePromoCodeStatus(
                member.status,
                member.codeStartDate,
                member.codeEndDate,
                offer.promos[0],
                offer.isNewFlow,
                offer.archivedDate,
                member.deletedDate,
              ),
              shopifyShowData: {
                shopifyStoreName,
                selectedPromo: member,
              },
              ...(offer.isPromoLink && linkForPromo ? {
                clicks: member.clicks,
                affiliateShortLink: member.affiliateShortLink,
              } : {}),
            };
          case OFFER_SOURCE.TUNE:
            return {
              ...commonFields,
              // TODO: behind ff for link currency conversion
              ...(toggleCurrency
                ? {
                  avgSale: {
                    valueBase: member.avgSaleBase,
                    valueUsd: member.avgSale,
                    currency: member.currencies[0],
                  },
                  sales: {
                    valueBase: member.salesBase,
                    valueUsd: member.sales,
                    currency: member.currencies[0],
                  },
                  payoutEarned: {
                    valueBase: member.payoutEarnedBase,
                    valueUsd: member.payoutEarned,
                    currency: member.currencies[0],
                  },
                }
                : {}),
              affiliateLink: member.affiliateLink,
              affiliateShortLink: member.affiliateShortLink,
              affiliateLinkAbbrev: {
                shortAffiliateLinkAbbrev: member.affiliateShortLink || abbreviateAffiliateLink(member.affiliateLink),
                shortLink: member.affiliateShortLink,
                defaultDeeplink: member.defaultDeeplink,
              },
              clicks: member.clicks,
              linkStatus: composeLinkStatus(offer, member.status, member.deletedDate),
              linkStatusError: member.linkStatusError,
              linkCreationDate: member.linkCreationDate,
              additionalDeepLinks: member.additionalDeepLinks,
              selectedAffliate: {
                affiliateOfferId: member.affiliateOfferId,
                memberAffiliateOfferId: member.memberAffiliateOfferId,
                name: member.name,
                source: member.source,
                imageUrl: member.imageUrl,
                isMultipleShopifyOffer: !!offer.promos[0]?.connectedClientMetadata?.length,
                status: member.status,
                payout: member?.payoutId ? payouts.find((p) => p.id === member.payoutId) : null,
                uniqueLink: member.affiliateShortLink,
                affliateLevelStats: {
                  conversions: member.conversions,
                  sales: member.sales,
                  avgSale: member.avgSale,
                  payoutEarned: member.payoutEarned,
                  payoutMade: member.payoutMade,
                  payOutDue: member.payoutEarned - member.payoutMade,
                  clicks: member.clicks,
                },
              },
            };
        }
      },
    );
  }, [
    filteredMembers,
    affiliatesStats,
    showStoreLevelAffiliateStats,
    missingShopifyCredentials,
    toggleCurrency,
    payouts,
    offer,
    shopifyStoreName,
    linkForPromo,
  ]);
  const filteredData = useMemo(() => {
    if (!searchText) return data;
    return data.filter((item: TMemberTableRow) =>
      item.affiliateName.name.toLowerCase().includes(searchText.toLowerCase()) ||
      item?.externalCode?.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [data, searchText]);
  const sortedData = React.useMemo(() => {
    if (!sorting.length) return filteredData;
    const [{ id, desc }] = sorting;
    return [...filteredData].sort((a, b) => {
      const aValue = a._sortableFields?.[id] ?? a[id as keyof typeof a];
      const bValue = b._sortableFields?.[id] ?? b[id as keyof typeof b];
      if (aValue === bValue) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return desc ? bValue - aValue : aValue - bValue;
      }
      return desc
        ? String(bValue).localeCompare(String(aValue))
        : String(aValue).localeCompare(String(bValue));
    });
  }, [filteredData, sorting]);
  useEffect(() => {
    const selectedRows = sortedData.filter((row) => rowSelection[row.id]);
    onSelectMembers(selectedRows.map((row) => Number(row.id)));
    setSelectedMember(selectedRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection]);

  React.useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
    if (page > totalPages) setPage(1);
  }, [filteredData.length, page, pageSize]);

  const totalPages = Math.ceil(sortedData.length / pageSize);
  const paginatedData = React.useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, page, pageSize]);

  // Memoized values
  const pendingMembers = useMemo(() => members.filter(memberIsPending), [members]);
  useEffect(() => {
    const currentlyPending = isOperationPending(pendingMembers);

    // If an operation starts
    if (currentlyPending && !hasPendingOperation) {
      setHasPendingOperation(true);
      const currentAction = isDeletedAction ? PendingAction.Deleting : getPendingActionFromMembers(pendingMembers);
      setSuccessNotice((prev) => ({
        ...prev,
        count: pendingMembers.length,
        action: currentAction,
        show: false,
      }));
    } else if (!currentlyPending && hasPendingOperation) {
      setHasPendingOperation(false);
      setSuccessNotice((prev) => ({
        ...prev,
        show: true,
      }));
    }
  }, [pendingMembers, hasPendingOperation, isDeletedAction]);
  const pendingMemberCount = pendingMembers.length;
  const handleDismissNotice = useCallback(() => {
    setSuccessNotice((prev) => ({
      ...prev,
      show: false,
    }));
    setIsDeletedAction(false);
  }, []);
  const deletedMessage = `Hi, I hope this message finds you well.\n\nAfter careful consideration, we have decided to discontinue your participation in ${offer.name} program. If you had any outstanding commission payments, please rest assured that we have processed those in full.\n\nThank you for your understanding.\n\nBest regards`;
  const isActive = offerStatus === OFFER_STATUS.ACTIVE;
  const disableButtons = !isActive || isEmpty(members) || pendingMemberCount > 0;
  const isLinkOffer = !isEmpty(offer.links) && !offer.isPromoLink;
  const isOfferMigrated = (offerSource === OFFER_SOURCE.SHOPIFY && offer.isNewFlow && offer.promos[0].defaultPayoutId)
    || (offerSource === OFFER_SOURCE.TUNE && offer.links[0].defaultPayoutId);
  const disableButtonForMigration = !isOfferMigrated;
  const toggleShowCurrencyButton = useCallback(() => {
    setToggleCurrency(!toggleCurrency);
  }, [toggleCurrency]);
  useEffect(() => {
    setRenderWithCurrency(// TODO: behind ff for link currency conversion
      (offerSource === OFFER_SOURCE.SHOPIFY
        ? isMulticurrency : currencyConversionForLink)
      && (props.currencies.length === 1 && !includes(props.currencies, 'USD')),
    );
  }, [isMulticurrency, props.currencies, offerSource, currencyConversionForLink]);
  const handleDeleteMembers = () => {
    Modal.confirm({
      title: `Remove ${selectedMember?.length || 0} ${selectedMember?.length === 1 ? 'member' : 'members'} from this offer?`,
      icon: <TriangleExclamationIcon />,
      content: (
        <div>
          <p>Please ensure there is no commission payout due for the selected member(s) you are trying to remove.</p>
          <p className="font-bold">
            <span>Note:</span> Once removed, you will not be able to add the member(s) back to this offer. This action cannot be undone.
          </p>
        </div>
      ),
      okType: 'danger',
      cancelText: 'Cancel',
      okText: 'Remove',
      onOk: async () => {
        setIsDeletedAction(true);
        const selectedMemberIds = selectedMember.map((row) => Number(row.affiliateOffer.id));
        const offerId = offer.promos[0]?.offerId || offer.links[0]?.defaultPayoutId;
        try {
          await buttonActions.deleteAffiliatesFromAnOffer(selectedMemberIds, offerId, offerSource, setIsDeletedAction);

          onComplete();
          onSelectMembers([]);
          setSelectedMember([]);
        } catch (error) {
          setIsDeletedAction(false);
          message.error('Failed to remove members from the offer');
        }
      },
    });
  };

  // Update row selection logic
  const handleRowClick = useCallback(
    (row: TMemberTableRow) => {
      if (row.disableSelection) return;

      setRowSelection((prev) => {
        if (prev[row.id]) return prev;
        return { [row.id]: true };
      });

      onSelectMembers([Number(row.id)]);
      setSelectedMember([row]);
    },
    [onSelectMembers, setSelectedMember]
  );

  const columns = useMemo(() => [
    {
      id: 'affiliateName',
      accessorKey: 'affiliateName',
      header: ({ column }) => <SortButton column={column}>Name</SortButton>,
      headerName: 'Name',
      cell: ({ row }) => (
        <Tooltip>
          <TooltipTrigger>
            <NameCell
              name={row.original.affiliateName.name}
              imageUrl={row.original.affiliateName.imageUrl}
              memberId={row.original.affiliateName.memberId}
              disabled={row.original.affiliateName.disabled}
              disabledReason={row.original.affiliateName.disabledReason}
            />
          </TooltipTrigger>
          <TooltipContent side="right" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]">
            <p className="m-0">{row.original.affiliateName.name}</p>
          </TooltipContent>
        </Tooltip>
      ),
      size: 280,
      minSize: 280,
      maxSize: 280,
      enableSorting: true,
    },
    ...(offerSource === OFFER_SOURCE.SHOPIFY ? [{
      id: 'externalCode',
      accessorKey: 'externalCode',
      header: ({ column }) => <SortButton column={column}>Promo Code</SortButton>,
      headerName: 'Promo Code',
      cell: ({ row }) => (
        <Tooltip>
          <TooltipTrigger>
            <div className="flex items-center w-48 min-h-[24px] gap-2">
              <span className="truncate w-[120px]">
                {row.original.externalCode.length > 15
                  ? `${row.original.externalCode.substring(0, 15)}...`
                  : row.original.externalCode}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 hover:bg-grey-2"
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(row.original.externalCode)
                    .then(() => {
                      message.success('Promo code copied to clipboard');
                    })
                    .catch(() => {
                      message.error('Failed to copy promo code');
                    });
                }}
                aria-label="Copy promo code"
              >
                <CopyIcon className="w-4 h-4 text-primary" />
              </Button>
            </div>
          </TooltipTrigger>
          <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 w-max">
            {row.original.externalCode}
          </TooltipContent>
        </Tooltip>
      ),
      size: 200,
      minSize: 200,
      maxSize: 200,
      enableSorting: true,
    }] : []),
    ...(offer.isPromoLink && linkForPromo ? [{
      id: 'affiliateShortLink',
      accessorKey: 'affiliateShortLink',
      header: ({ column }) => <SortButton column={column}>{offer.isLandingPageEnabled ? 'CreatorStore URL' : 'Link'}</SortButton>,
      headerName: offer.isLandingPageEnabled ? 'CreatorStore URL' : 'Link',
      cell: ({ row }) => (
        <Tooltip>
          <TooltipTrigger>
            <a
              href={row.original.affiliateShortLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              {row.original.affiliateShortLink}
            </a>
          </TooltipTrigger>
          <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[300px] break-all">
            {row.original.affiliateLink}
          </TooltipContent>
        </Tooltip>
      ),
      size: 240,
      minSize: 240,
      maxSize: 240,
      enableSorting: true,
    }] : []),
    ...(offerSource === OFFER_SOURCE.TUNE ? [{
      id: 'affiliateLinkAbbrev',
      accessorKey: 'affiliateLinkAbbrev',
      headerName: 'Primary Link',
      header: ({ column }) => (
        <div className="flex items-center gap-2">
          <SortButton column={column}>Primary Link</SortButton>
          <Tooltip>
            <TooltipTrigger>
              <InfoCircleFilled className="text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]">
              Primary Links are unique deep links you assign when adding a member to an offer
            </TooltipContent>
          </Tooltip>
        </div>
      ),
      cell: ({ row }) => (
        <Tooltip>
          <TooltipTrigger>
            <a
              href={row.original.affiliateLinkAbbrev?.shortLink || row.original.affiliateLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              {row.original.affiliateLinkAbbrev?.shortAffiliateLinkAbbrev}
            </a>
          </TooltipTrigger>
          <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[300px] break-all">
            {row.original.affiliateLink}
          </TooltipContent>
        </Tooltip>
      ),
      size: 240,
      minSize: 240,
      maxSize: 240,
    }] : []),
    ...(offerSource === OFFER_SOURCE.TUNE ? [{
      id: 'linkCreationDate',
      accessorKey: 'linkCreationDate',
      header: ({ column }) => <SortButton column={column}>Link Created</SortButton>,
      headerName: 'Link Created',
      cell: ({ row }) => toLocale(row.original.linkCreationDate),
      size: 170,
      minSize: 170,
      maxSize: 170,
      enableSorting: true,
    }] : []),
    {
      id: 'linkStatus',
      accessorKey: 'linkStatus',
      header: ({ column }) => <SortButton column={column}>Status</SortButton>,
      headerName: 'Status',
      cell: ({ row }) => renderStatusColumn(row.original.linkStatus, row.original.linkStatusError, isDeletedAction),
      size: 110,
      minSize: 110,
      maxSize: 110,
      enableSorting: true,
      filterFn: (row, value) => {
        const predefinedStatuses = isLinkOffer ? LINK_FILTERS.map((f) => f.value) : PROMO_FILTERS.map((f) => f.value);
        return value === 'Others' ? !predefinedStatuses.includes(row.original.linkStatus) : row.original.linkStatus === value;
      },
    },
    ...(offer.promos[0]?.isUngrouped ? [
      {
        id: 'startDate',
        accessorKey: 'startDate',
        header: ({ column }) => <SortButton column={column}>Start Date</SortButton>,
        headerName: 'Start Date',
        cell: ({ row }) => toLocale(row.original.startDate),
        size: 165,
        minSize: 165,
        maxSize: 165,
        enableSorting: true,
      },
      {
        id: 'endDate',
        accessorKey: 'endDate',
        header: ({ column }) => <SortButton column={column}>End Date</SortButton>,
        headerName: 'End Date',
        cell: ({ row }) => toLocale(row.original.endDate),
        size: 165,
        minSize: 165,
        maxSize: 165,
        enableSorting: true,
      },
    ] : []),
    {
      id: 'clicks',
      accessorKey: 'clicks',
      header: ({ column }) => <SortButton column={column}>Clicks</SortButton>,
      headerName: 'Clicks',
      cell: ({ row }) => (
        <div className="text-right w-full">{formatValue(DataFormat.Integer, row.original.clicks,)}</div>
      ),
      size: 100,
      minSize: 100,
      maxSize: 100,
      enableSorting: true,
    },
    {
      id: 'conversions',
      accessorKey: 'conversions',
      header: ({ column }) => <SortButton column={column}>Conversions</SortButton>,
      headerName: 'Conversions',
      cell: ({ row }) => (
        <div className="text-right w-full">{formatValue(DataFormat.Integer, row.original.conversions)}</div>
      ),
      size: 160,
      minSize: 160,
      maxSize: 160,
      enableSorting: true,
    },
    {
      id: 'sales',
      accessorKey: 'sales',
      header: ({ column }) => <SortButton column={column}>Sales Amt</SortButton>,
      headerName: 'Sales Amt',
      cell: ({ row }) => (
        <div className="text-right w-full">{renderCurrencyColumn(row.original.sales, toggleCurrency)}</div>
      ),
      size: 150,
      minSize: 150,
      maxSize: 150,
      enableSorting: true,
    },
    {
      id: 'avgSale',
      accessorKey: 'avgSale',
      header: ({ column }) => <SortButton column={column}>Avg. Sale</SortButton>,
      headerName: 'Avg. Sale',
      cell: ({ row }) => (
        <div className="text-right w-full">{row.original.avgSale ? renderCurrencyColumn(row.original.avgSale, toggleCurrency) : '$0.00'}</div>
      ),
      size: 150,
      minSize: 150,
      maxSize: 150,
      enableSorting: true,
    },
    {
      id: 'payoutEarned',
      accessorKey: 'payoutEarned',
      header: ({ column }) => <SortButton column={column}>Commission Earned</SortButton>,
      headerName: 'Commission Earned',
      cell: ({ row }) => (
        <div className="text-right w-full">{renderCurrencyColumn(row.original.payoutEarned, toggleCurrency)}</div>
      ),
      size: 220,
      minSize: 220,
      maxSize: 220,
      enableSorting: true,
    },
    {
      id: 'payoutMade',
      accessorKey: 'payoutMade',
      header: ({ column }) => <SortButton column={column}>Payout</SortButton>,
      headerName: 'Payout',
      cell: ({ row }) => (
        <div className="text-right w-full">${formatValue(DataFormat.Money, row.original.payoutMade)}</div>
      ),
      size: 150,
      minSize: 150,
      maxSize: 150,
      enableSorting: true,
    },
    ...(offerSource === OFFER_SOURCE.SHOPIFY ? [{
      id: 'createdDate',
      accessorKey: 'createdDate',
      header: ({ column }) => <SortButton column={column}>Created Date</SortButton>,
      headerName: 'Created Date',
      cell: ({ row }) => (row.original.linkCreationDate ? format(new Date(row.original.linkCreationDate), 'MM/dd/yyyy, H:mm') : ''),
      size: 170,
      minSize: 170,
      maxSize: 170,
      enableSorting: true,
    }] : []),
    {
      id: 'memberId',
      accessorKey: 'memberId',
      header: ({ column }) => <SortButton column={column}>Member Id</SortButton>,
      headerName: 'Member Id',
      cell: ({ row }) => <div className="text-right w-full">{row.original.memberId}</div>,
      size: 170,
      minSize: 170,
      maxSize: 170,
      enableSorting: true,
    },
    // Add additional deeplinks column if applicable
    ...(isManageDeepLinksAllowed && isLinkOffer ? [{
      id: 'additionalDeepLinks',
      accessorKey: 'additionalDeepLinks',
      header: ({ column }) => <SortButton column={column}>Additional Link</SortButton>,
      headerName: 'Additional Link',
      cell: ({ row }) => (
        <AdditionalDeeplinkTags
          affiliateOfferLinkRecord={row.original}
          setDeeplinkData={setDeeplinkData}
          setDeeplinkDrawerVisible={setDeeplinkDrawerVisible}
        />
      ),
      size: 240,
      minSize: 240,
      maxSize: 240,
      enableSorting: false,
    }] : []),
    // Add payout type column if migrated
    ...(isOfferMigrated ? [{
      id: 'payoutId',
      accessorKey: 'payoutId',
      header: ({ column }) => <SortButton column={column}>Payout Type</SortButton>,
      headerName: 'Payout Type',
      cell: ({ row }) => {
        if (automatePayoutTier && row.original.payoutId?.automationConfigId) {
          return <Tag label="Automated Tier" bgColor="bg-grey-2" textColor="text-primary" />;
        }
        return renderPayoutVariant(row.original.payoutId?.id, true, payouts);
      },
      size: 200,
      minSize: 200,
      maxSize: 200,
      enableSorting: true,
    }] : []),
    // Add stats column if migrated
    ...(isOfferMigrated ? [{
      id: 'selectedAffliate',
      accessorKey: 'selectedAffliate',
      header: ({ column }) => <SortButton column={column}>Stats</SortButton>,
      headerName: 'Stats',
      cell: ({ row }) => renderStatsColumn(row.original.selectedAffliate, buttonActions),
      size: 100,
      minSize: 100,
      maxSize: 100,
      enableSorting: true,
    }] : []),
    // Add Shopify column
    ...(offerSource === OFFER_SOURCE.SHOPIFY ? [{
      id: 'shopifyShowData',
      accessorKey: 'shopifyShowData',
      header: ({ column }) => <SortButton column={column}>Shopify</SortButton>,
      headerName: 'Shopify',
      cell: ({ row }) => renderShopifyColumn(shopifyStoreName, row.original.shopifyShowData?.selectedPromo, offer, offerSource),
      size: 100,
      minSize: 100,
      maxSize: 100,
      enableSorting: false,
    }] : []),
    // Grouped columns for each store
    ...(offerSource === OFFER_SOURCE.SHOPIFY && showStoreLevelAffiliateStats ? connectedStores.map((store) => ({
      id: `store-${store.clientShopifyConnectionId}`,
      header: () => <div className="w-full text-center font-semibold">{store.storeName}</div>,
      headerName: store.storeName,
      columns: [
        {
          id: `store-${store.clientShopifyConnectionId}-conversions`,
          accessorKey: 'affliateDetailedStats',
          header: ({ column }) => <SortButton column={column}>Conversion</SortButton>,
          cell: ({ row }) => {
            const filteredStore = row.original.affliateDetailedStats?.find(
              (d) => d.clientShopifyConnectionId === store.clientShopifyConnectionId
            );
            return <div className="text-right w-full">{formatValue(DataFormat.Integer, filteredStore?.conversions || 0)}</div>;
          },
          size: 160,
          minSize: 160,
          maxSize: 160,
          enableSorting: true,
        },
        {
          id: `store-${store.clientShopifyConnectionId}-sales`,
          accessorKey: 'affliateDetailedStats',
          headerName: 'Sales Amt',
          header: ({ column }) => <SortButton column={column}>Sales Amt</SortButton>,
          cell: ({ row }) => {
            const filteredStore = row.original.affliateDetailedStats?.find(
              (d) => d.clientShopifyConnectionId === store.clientShopifyConnectionId
            );
            return (
              <div className="text-right w-full">
                {filteredStore?.sales
                  ? renderColumsWithBase('$', filteredStore.sales, filteredStore.salesBase, filteredStore.currency)
                  : '$0.00'}
              </div>
            );
          },
          size: 150,
          minSize: 150,
          maxSize: 150,
          enableSorting: true,
        },
        {
          id: `store-${store.clientShopifyConnectionId}-avgSale`,
          accessorKey: 'affliateDetailedStats',
          headerName: 'Avg. Sales',
          header: ({ column }) => <SortButton column={column}>Avg. Sales</SortButton>,
          cell: ({ row }) => {
            const filteredStore = row.original.affliateDetailedStats?.find(
              (d) => d.clientShopifyConnectionId === store.clientShopifyConnectionId
            );
            return (
              <div className="text-right w-full">
                {filteredStore?.avgSale
                  ? renderColumsWithBase('$', filteredStore.avgSale, filteredStore.avgSaleBase, filteredStore.currency)
                  : '$0.00'}
              </div>
            );
          },
          size: 150,
          minSize: 150,
          maxSize: 150,
          enableSorting: true,
        },
        {
          id: `store-${store.clientShopifyConnectionId}-payoutEarned`,
          accessorKey: 'affliateDetailedStats',
          headerName: 'Commission Earned',
          header: ({ column }) => <SortButton column={column}>Commission Earned</SortButton>,
          cell: ({ row }) => {
            const filteredStore = row.original.affliateDetailedStats?.find(
              (d) => d.clientShopifyConnectionId === store.clientShopifyConnectionId
            );
            return (
              <div className="text-right w-full">
                {filteredStore?.payoutEarned
                  ? renderColumsWithBase('$', filteredStore.payoutEarned, filteredStore.payoutEarnedBase, filteredStore.currency)
                  : '$0.00'}
              </div>
            );
          },
          size: 220,
          minSize: 220,
          maxSize: 220,
          enableSorting: true,
        },
      ],
    })) : []),
  ], [
    offer,
    offerSource,
    isLinkOffer,
    isManageDeepLinksAllowed,
    isOfferMigrated,
    payouts,
    shopifyStoreName,
    buttonActions,
    setDeeplinkData,
    setDeeplinkDrawerVisible,
    isDeletedAction,
    toggleCurrency,
    showStoreLevelAffiliateStats,
    connectedStores,
    linkForPromo,
    automatePayoutTier,
  ]);

  // Add sorting handler
  const handleSortingChange = useCallback(
    (newSorting: SortingState) => {
      setSorting(newSorting);
      // Reset to first page when sorting changes
      setPage(1);
    },
    [setPage],
  );
  const selectedMembers = useMemo(() => {
    if (selectedAffiliateIds.length === 0) {
      return data;
    }
    const selectedMemberIdSet = new Set(selectedAffiliateIds);
    return data.filter((row) => selectedMemberIdSet.has(Number(row.id)));
  }, [data, selectedAffiliateIds]);
  const payoutConversionText = useCallback(
    (payoutId) => {
      if (automatePayoutTier && payoutId?.automationConfigId) {
        return 'Automated Tier';
      }
      return renderPayoutVariant(payoutId.id as number, false, payouts) as string;
    },
    [automatePayoutTier, payouts],
  );
  const { csvColumnConfig, csvData } = useMemo(
    () => ({
      csvColumnConfig: [
        { headerName: 'No.', field: 'number' },
        ...columns
          .map((config) => {
            if (!isString(config.id)) {
              return null;
            }
            const field = config.id.toString();
            switch (config.id) {
              case 'affiliateLinkAbbrev':
                return null;
              case 'selectedAffliate':
                return null;
              case 'additionalDeepLinks':
                return null;
              case 'shopifyShowData':
                return null;
              // Skip store-level group columns
              case 'payoutId':
                return {
                  headerName: 'Payout Type',
                  field: 'csvPayoutId',
                };
              default:
                if (typeof config.id === 'string' && config.id.startsWith('store-')) {
                  return null;
                }
            }
            return {
              headerName: config.headerName as string,
              field,
            };
          })
          .concat({
            headerName: 'Email',
            field: 'email',
          })
          .concat({
            headerName: 'Primary Link',
            field: 'affiliateLink',
          })
          .concat({
            headerName: 'Shortened Link',
            field: 'affiliateShortLink',
          })
          .concat({
            headerName: 'Additional Link',
            field: 'additionalLink',
          })
          .filter((c) => !isNull(c))
          .filter((c) => c.headerName !== 'Shopify'),
      ],
      csvData: map(
        selectedMembers,
        (row, index): TMemberTableCSVRow => {
          const { shopifyShowData: _shopifyShowData, ...rest } = row;
          const parseAvgSale = (value) => {
            if (!value) return 0;
            // Remove commas and convert to number
            const cleanValue = String(value).replace(/,/g, '');
            const numValue = Number(cleanValue);
            return isNaN(numValue) ? 0 : numValue;
          };
          return ({
            ...rest,
            number: index + 1,
            activeCode: row.externalCode || '',
            affiliateName: row.affiliateName.name,
            email: row.email,
            clicks: row.clicks ?? 0,
            conversions: row.conversions ?? 0,
            startDate: row.startDate ? format(new Date(row.startDate), 'MM/dd/yyyy, H:mm') : '',
            endDate: row.endDate ? format(new Date(row.endDate), 'MM/dd/yyyy, H:mm') : '',
            linkCreationDate: row.linkCreationDate ? format(new Date(row.linkCreationDate), 'MM/dd/yyyy, H:mm') : '',
            csvPayoutId: payoutConversionText(row.payoutId),
            avgSale: parseAvgSale(row.avgSale),
            additionalLink: Array.isArray(row.additionalDeepLinks) ? row.additionalDeepLinks.map((link) => link.url).join(', ') : '',
            memberId: row.memberId,
          });
        },
      ),
    }),
    [columns, payoutConversionText, selectedMembers],
  );
  const onClickExport = useCallback(() => {
    buttonActions.export(csvColumnConfig, csvData);
  }, [buttonActions, csvColumnConfig, csvData]);
  const failedMembers = useMemo(() => filter(members, (m) => m.status === OfferMemberStatus.FAILED), [members]);
  // Render methods
  const renderHeader = () => (
    <div className="flex items-center justify-between py-4 px-6">
      {/* LEFT: Member count and main actions */}
      <div className="flex items-center gap-x-2">
        <span className="mr-2">{`${filteredData.length} Members`}</span>
        {isNull(offer.archivedDate) && (
          <Tooltip>
            <TooltipTrigger>
              <Button
                disabled={selectedAffiliateIds.length > 0 || pendingMemberCount > 0 || !isActive || disableButtonForMigration}
                onClick={buttonActions.addMembers}
                variant="ghost"
                size="icon"
                aria-label="Add Members"
                className="h-8 w-8"
              >
                <UserPlusIcon className="h-4 w-4 text-grey-6" />

              </Button>
            </TooltipTrigger>
            <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]">
              Add Members
            </TooltipContent>
          </Tooltip>
        )}
        {isNull(offer.archivedDate)
          && ((offerSource === OFFER_SOURCE.SHOPIFY && offer.promos[0].defaultPayoutId)
            || (offerSource === OFFER_SOURCE.TUNE && offer.links[0].defaultPayoutId))
          && (
            <Tooltip>
              <TooltipTrigger>
                <Button
                  disabled={disableButtons || !selectedAffiliateIds.length}
                  onClick={buttonActions.refresh}
                  variant="ghost"
                  size="icon"
                  aria-label="Manage Offer"
                  className="h-8 w-8"
                >
                  <FontAwesomeIcon icon={faPencil} className="text-grey-6" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]">
                {disableButtons || !selectedAffiliateIds.length ? 'Select members to edit in bulk' : 'Manage Offer'}
              </TooltipContent>
            </Tooltip>
          )}
        {renderWithCurrency && !connectedStores.length && (
          <Tooltip>
            <TooltipTrigger>
              <Button
                disabled={disableButtons}
                onClick={toggleShowCurrencyButton}
                variant="ghost"
                size="icon"
                aria-label="Manage Offer"
                className="h-8 w-8"
              >
                <SplitIcon className="h-4 w-4 text-grey-6" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]">
              View Store Data Conversion
            </TooltipContent>
          </Tooltip>
        )}
        {offerSource === OFFER_SOURCE.SHOPIFY
          && isEnabledMultipleShopify
          && !!connectedStores.length
          && (
            <Tooltip>
              <TooltipTrigger>
                <Button
                  onClick={() => {
                    buttonActions.storeLevelAffiliateStats();
                    onSelectMembers([]);
                  }}
                  variant="ghost"
                  size="icon"
                  aria-label="Manage Offer"
                  className="h-8 w-8"
                >
                  <SplitIcon className="h-4 w-4 text-grey-6" />
                </Button>
                <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]">
                  <p>View Store Data Conversion</p>
                </TooltipContent>
              </TooltipTrigger>
            </Tooltip>
          )}
        {emailComposeButton}
        {isManageDeepLinksAllowed && offer.links[0] && !offer.isPromoLink && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <EllipsisIcon className="h-4 w-4 text-grey-6" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">

              <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="p-0">
                <div className="w-full" onClick={(e) => e.stopPropagation()}>
                  <div className="px-2 py-1.5">
                    <RefreshCreateAdditionalDeepLinks
                      offer={offer}
                      selectedMembers={selectedMembers}
                      selectedAffiliateIds={selectedAffiliateIds}
                      onComplete={onComplete}
                      className="relative z-[100]"
                    />
                  </div>
                </div>
              </DropdownMenuItem>
              {deleteCreatorFromOffer && (
                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                  <div className="w-full" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      onClick={handleDeleteMembers}
                      className="text-destructive hover:text-destructive w-full justify-start"
                      disabled={selectedAffiliateIds.length === 0}
                    >
                      <TrashCanIcon className="mr-2 h-4 w-4" />
                      Remove from Offer
                    </Button>
                  </div>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {/* RIGHT: Search, Export, Column Selector, Pagination */}
      <div className="flex items-center gap-x-2 flex-grow justify-end">
        {/* Search */}
        <div className="relative flex items-center">
          <Button
            variant="ghost"
            className="w-8 h-8 flex items-center justify-center z-20"
            size="icon"
            onClick={() => setIsSearchOpen((v) => !v)}
            aria-label="Search"
          >
            {isSearchOpen ? (
              <XmarkIcon className="text-muted-foreground" />
            ) : (
              <SearchOutlined className="text-muted-foreground" />
            )}
          </Button>
          <div
            className={`absolute right-10 top-0 transition-all duration-300 ${isSearchOpen ? 'opacity-100 w-48' : 'opacity-0 w-0'} overflow-hidden`}
            style={{ zIndex: 10 }}
          >
            <Input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              type="search"
              placeholder="Search Offer"
              className="rounded-lg bg-background pl-8 w-48 focus:ring-0 focus-visible:ring-0 outline-none"
              autoFocus={isSearchOpen}
            />
            <SearchOutlined className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          </div>
        </div>
        {/* Export */}
        <Tooltip>
          <TooltipTrigger>
            <Button
              disabled={isEmpty(data)}
              onClick={onClickExport}
              variant="ghost"
              size="icon"
              aria-label="Export members"
            >
              <ArrowDownToLineIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]">
            Export members
          </TooltipContent>
        </Tooltip>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              disabled={isEmpty(data)}
              aria-label="Toggle column visibility"
            >
              <TableColumnsIcon />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-56 p-4">
            <div className="flex flex-col gap-2">
              <h4 className="font-medium mb-2">Visible Columns</h4>
              {columns.map((column) => (
                <label key={column.id} className="flex items-center gap-2">
                  <Checkbox
                    checked={columnVisibility[column.id] !== false}
                    disabled={column.id === 'affiliateName'}
                    onCheckedChange={(checked) => {
                      setColumnVisibility((prev) => ({
                        ...prev,
                        [column.id]: checked === true,
                      }));
                    }}
                  />
                  <span>{column.headerName}</span>
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        {/* Pagination */}
        <TablePagination
          pageIndex={page - 1}
          pageSize={pageSize}
          totalCount={filteredData.length}
          canPreviousPage={page > 1}
          canNextPage={page < totalPages}
          onPaginationChange={(newPage) => setPage(newPage + 1)}
        />
      </div>
    </div>
  );

  if (isEmpty(data)) {
    return (
      <div>
        <ZeroState
          missingShopifyCredentials={missingShopifyCredentials}
          offer={offer}
          offerSource={offerSource}
          offerStatus={offerStatus}
          onClickGenerateLink={buttonActions.addMembers}
          disableAddMemberButton={disableButtonForMigration || !isNull(offer.archivedDate)}
        />
      </div>
    );
  }

  return (
    <>
      {!missingShopifyCredentials && isNull(offer.archivedDate) && (
        <FailedNotice
          message={<PromoCodeErrorMessage errors={props.failedPromoCodeErrors} />}
          onClickFix={fixFailedMembers}
          visible={failedMembers.length !== 0}
          disabled={!!pendingMemberCount}
        />
      )}
      <PendingMembers pendingMembers={pendingMembers} offerSource={offerSource} isDeletedAction={isDeletedAction} offer={offer} />

      {failedMembers.length === 0 && successNotice.show && successNotice.action && deleteCreatorFromOffer && (
        <SuccessNotice
          onClose={handleDismissNotice}
          offerSource={offerSource}
          successNoticeCount={successNotice.count}
          successNoticeAction={successNotice.action}
          members={selectedMember}
          subject={`Update on Your Participation in ${offer.name} Offer Program`}
          initialMessage={deletedMessage}
          source="success-notice"
          onMessageSent={() => console.log('Email sent')}
          setIsDeletedAction={setIsDeletedAction}
          isDeletedAction={isDeletedAction}
        />
      )}
      <Card className="h-[65vh] overflow-y-auto flex flex-col">
        {renderHeader()}
        <div className="overflow-y-auto border-t">
          <DataTable
            columns={columns}
            data={paginatedData}
            enableColumnResizing
            getRowId={(row) => row.id.toString()}
            onRowClick={handleRowClick}
            className="min-w-[900px] [&_td]:py-1 [&_td]:h-10! [&_td]:max-h-10! [&_td]:align-middle"
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            onSortingChange={handleSortingChange}
            enableRowSelection
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            pinnedLeft={['affiliateName']}
          // key={columnVisibility}
          />
        </div>

        {isManageDeepLinksAllowed && deeplinkData && (
          <DeeplinkDrawer
            memberData={deeplinkData}
            setDeeplinkDrawerVisible={setDeeplinkDrawerVisible}
            isDeeplinkDrawerVisibile={isDeeplinkDrawerVisible}
            offer={offer}
            refresh={onComplete}
          />
        )}
      </Card>
    </>
  );
};
